<template>
  <FieldSalesDashboard v-if="isFieldStaff" />
  <StorekeeperDashboard v-else-if="isStorekeeperStaff" />
  <TelesalesDashboard v-else-if="isTelesalesStaff" />
  <div v-else class="dashboard-page mt-3">
    <CCard class="dashboard-card border-0 shadow-sm mt-3">
      <!-- <CCardHeader class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <strong>Dashboard</strong>
          <div class="small text-muted">Sales and call performance overview</div>
        </div>
        <div class="small text-muted mt-2 mt-sm-0">
          Last updated: {{ lastUpdatedLabel }}
        </div>
      </CCardHeader> -->
      <CCardBody>
        <CAlert v-if="errorMessage" color="danger" show>
          {{ errorMessage }}
        </CAlert>

        <div class="filter-bar mb-3">
          <DateRangePicker
            v-model="filters"
            combined
            :disabled="isLoading"
            @preset="applyFilters"
          />
          <div class="filter-actions">
            <CButton
              color="primary"
              class="action-btn"
              :disabled="isLoading"
              @click="applyFilters"
              data-cta-sound="on"
            >
              {{ isLoading ? 'Loading...' : 'Search' }}
            </CButton>
            <CButton
              color="dark"
              variant="outline"
              class="action-btn reload-icon-btn"
              :disabled="isLoading"
              title="Reload"
              aria-label="Reload"
              @click="reloadData"
            >
              <span class="reload-glyph" aria-hidden="true">&#x21bb;</span>
            </CButton>
          </div>
        </div>

        <CRow class="tight-row tiles-row pt-3">
          <!-- Initial-load skeletons. Once tiles are populated the
               real cards take over (a refresh-fetch keeps showing
               the previous tiles instead of flashing the skeletons). -->
          <template v-if="isLoading && !tileCards.length">
            <CCol
              v-for="i in skeletonTileCount"
              :key="`sk-${i}`"
              xl="3"
              lg="4"
              sm="6"
              col="6"
              class="mb-2 tile-col"
            >
              <div class="tile-skeleton" aria-hidden="true">
                <div class="tile-skeleton__body">
                  <div class="tile-skeleton__lines">
                    <div class="tile-skeleton__line tile-skeleton__line--value"></div>
                    <div class="tile-skeleton__line tile-skeleton__line--label"></div>
                  </div>
                  <div class="tile-skeleton__icon"></div>
                </div>
              </div>
            </CCol>
          </template>
          <CCol
            v-for="(card, index) in tileCards"
            :key="card.key"
            xl="3"
            lg="4"
            sm="6"
            col="6"
            class="mb-2 tile-col"
          >
            <button
              type="button"
              :class="['h-100 border-0 tile-card', tileCardClass(card.key, index), { 'is-selected': selectedTile === card.key }]"
              :aria-pressed="selectedTile === card.key"
              @click="onTileClick(card.key)"
            >
              <div class="tile-body">
                <div class="tile-text">
                  <div class="tile-value">{{ formatTileValue(card.key, card.value) }}</div>
                  <div class="tile-label">
                    {{ card.label }}
                    <span
                      v-if="tileInfo(card.key)"
                      class="tile-info"
                      role="button"
                      tabindex="0"
                      :aria-label="tileInfo(card.key)"
                      @click.stop
                      @keydown.enter.stop
                      @keydown.space.stop
                      @mouseenter="showTileInfo($event, card.key)"
                      @mouseleave="hideTileInfo"
                      @focus="showTileInfo($event, card.key)"
                      @blur="hideTileInfo"
                    >i</span>
                  </div>
                </div>
                <span class="tile-icon" aria-hidden="true">
                  <CIcon :name="tileIcon(card.key)" size="lg" />
                </span>
              </div>
            </button>
          </CCol>
        </CRow>

        <div class="reports-section pt-3">
          <div class="report-tabs-wrap">
            <button
              type="button"
              class="tab-scroll-btn tab-scroll-btn--left"
              :class="{ 'is-hidden': tabScrollAtStart }"
              aria-label="Scroll tabs left"
              @click="scrollReportTabs(-1)"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div ref="reportTabs" class="report-tabs" role="tablist" @scroll="updateTabScroll">
              <button
                v-for="tab in reportTabs"
                :key="tab.key"
                type="button"
                role="tab"
                class="report-tab"
                :class="{ 'is-active': tab.key === activeReport }"
                :aria-selected="tab.key === activeReport"
                @click="activeReport = tab.key"
              >
                <span class="tab-label">{{ tab.label }}</span>
                <span v-if="!isLoading && tab.items.length" class="tab-count">{{ tab.items.length }}</span>
              </button>
            </div>
            <button
              type="button"
              class="tab-scroll-btn tab-scroll-btn--right"
              :class="{ 'is-hidden': tabScrollAtEnd }"
              aria-label="Scroll tabs right"
              @click="scrollReportTabs(1)"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          <div v-if="activeReport === 'processed_returns' && returnsFilterActive && returnsFilterOptions.length > 1" class="returns-filter">
            <label class="returns-filter__label">Product</label>
            <AppDropdown
              v-model="returnsProductFilter"
              :options="returnsFilterOptions"
              :searchable="returnsFilterOptions.length > 8"
              search-placeholder="Search product…"
              class="returns-filter__select"
            />
          </div>

          <!-- Sales By Person: search + Excel export. The grid is driven by
               filteredSalesByPerson, so the table and the export always
               reflect the same visible rows. -->
          <div v-if="activeReport === 'sales'" class="sbp-controls">
            <div class="sbp-search">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                v-model="spSearch"
                type="text"
                class="sbp-search__input"
                placeholder="Search staff…"
                aria-label="Search staff by name"
              />
            </div>

            <!-- Detail level for the TABLE only. Summary (default, left) is the
                 readable executive view; Detailed (right) is the full audit
                 set. The Excel export always downloads Detailed regardless. -->
            <div class="sbp-level" role="group" aria-label="Table column detail level">
              <button
                v-for="opt in [{ v: 'summary', l: 'Summary' }, { v: 'detailed', l: 'Detailed' }]"
                :key="opt.v"
                type="button"
                :class="['sbp-level__btn', { 'is-active': spDetailLevel === opt.v }]"
                :aria-pressed="spDetailLevel === opt.v"
                @click="spDetailLevel = opt.v"
              >{{ opt.l }}</button>
            </div>

            <button
              type="button"
              class="sbp-export"
              :disabled="spExporting || !filteredSalesByPerson.length"
              @click="exportSalesByPerson"
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>{{ spExporting ? 'Exporting…' : 'Export to Excel' }}</span>
            </button>
          </div>

          <!-- Staff Returns: search + Excel export. The grid is driven by
               filteredStaffReturns, so the table and the export always
               reflect the same visible rows. -->
          <div v-if="activeReport === 'staff_return_report'" class="sbp-controls">
            <div class="sbp-search">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                v-model="srSearch"
                type="text"
                class="sbp-search__input"
                placeholder="Search staff…"
                aria-label="Search staff by name"
              />
            </div>

            <button
              type="button"
              class="sbp-export"
              :disabled="srExporting || !filteredStaffReturns.length"
              @click="exportStaffReturns"
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>{{ srExporting ? 'Exporting…' : 'Export to Excel' }}</span>
            </button>
          </div>

          <BaseTable
            :accent="activeTab.accent"
            :items="activeTab.items"
            :columns="activeTab.columns || null"
            :loading="isLoading"
            :empty-text="activeTab.emptyText"
            :show-count="false"
            :sticky-first-column="!!activeTab.stickyFirstColumn"
          >
            <!-- Biller-view cells: value is the biller's (or role's) own
                 count; co_* carries the c/o subset shown as subtext.
                 Rows from tabs that don't carry the co_* field fall
                 through to the plain formatted value. -->
            <!-- Role-Wise surfaces c/o as its own column, so suppress the
                 inline subtext there; Staff Returns still uses it. -->
            <template #cell-orders="{ row, value }">
              <template v-if="activeReport !== 'roles' && Number(row && row.co_orders) > 0">
                <div class="split-cell">
                  <div class="split-cell__value">{{ formatNumber(value) }}</div>
                  <div class="split-cell__sub">{{ formatNumber(row.co_orders) }} c/o</div>
                </div>
              </template>
              <template v-else>{{ formatNumber(value) }}</template>
            </template>
            <template #cell-sl_no="{ index }">{{ index + 1 }}</template>
            <template #cell-customer="{ row, value }">
              <div class="cust-cell">
                <div class="cust-cell__name">{{ value }}</div>
                <div v-if="row.customer_phone" class="cust-cell__phone">{{ row.customer_phone }}</div>
              </div>
            </template>
            <template #cell-products="{ value }">
              <ul v-if="Array.isArray(value) && value.length" class="ret-products">
                <li v-for="(prod, i) in value" :key="i" class="ret-products__item">
                  <span class="ret-products__name">{{ prod.name }}</span>
                  <span class="ret-products__qty">×{{ formatNumber(prod.quantity) }}</span>
                </li>
              </ul>
              <span v-else>—</span>
            </template>
            <!-- Staff Returns has a dedicated "C/O Orders Returned" column, so
                 suppress the inline handed-off subtext there; other tabs keep it. -->
            <template #cell-returns="{ row, value }">
              <template v-if="activeReport !== 'staff_return_report' && Number(row && row.co_returns) > 0">
                <div class="split-cell">
                  <div class="split-cell__value">{{ formatNumber(value) }}</div>
                  <div class="split-cell__sub">{{ formatNumber(row.co_returns) }} c/o</div>
                </div>
              </template>
              <template v-else>{{ formatNumber(value) }}</template>
            </template>
            <!-- Staff Returns c/o columns — a muted dash reads cleaner than a
                 column of zeros for staff with no care-of returns. -->
            <template #cell-co_orders_returned="{ value }">
              <span :class="{ 'co-muted': !Number(value) }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
            </template>
            <template #cell-co_bottles_returned="{ value }">
              <span :class="{ 'co-muted': !Number(value) }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
            </template>
            <template #cell-co_kgs_returned="{ value }">
              <span :class="{ 'co-muted': !Number(value) }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
            </template>
            <template #cell-co_farm_bottles_returned="{ value }">
              <span :class="{ 'co-muted': !Number(value) }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
            </template>
            <template #cell-order="{ value }">{{ formatNumber(value) }}</template>
            <!-- Dedicated c/o columns (Sales By Person + Role-Wise). A bare
                 dash reads cleaner than a column full of zeros for the many
                 rows with no care-of orders. -->
            <!-- Net c/o can be negative (orders billed but credited away, e.g.
                 a biller like MCCI Pharmacy): show those in red. -->
            <template #cell-co_order="{ value }">
              <span :class="{ 'co-muted': !Number(value), 'co-neg': Number(value) < 0 }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
            </template>
            <template #cell-co_orders="{ value }">
              <span :class="{ 'co-muted': !Number(value), 'co-neg': Number(value) < 0 }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
            </template>
            <template #cell-co_bottles="{ value }">
              <span :class="{ 'co-muted': !Number(value), 'co-neg': Number(value) < 0 }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
            </template>
            <template #cell-co_farm_bottles="{ value }">
              <span :class="{ 'co-muted': !Number(value), 'co-neg': Number(value) < 0 }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
            </template>
            <!-- C/O bottles is surfaced as its own column (Sales By Person),
                 so the Bottles column shows just the plain total — no inline
                 c/o subtext. -->
            <template #cell-bottles="{ value }">{{ formatNumber(value) }}</template>
            <!-- C/O kgs is surfaced as its own column, so Kgs Sold shows just
                 the plain total — no inline c/o subtext. -->
            <template #cell-kgs_sold="{ value }">{{ formatNumber(value) }}</template>
            <template #cell-co_kgs_sold="{ value }">
              <span :class="{ 'co-muted': !Number(value), 'co-neg': Number(value) < 0 }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
            </template>
            <template #cell-total_kgs_sold="{ value }">{{ formatNumber(value) }}</template>
            <!-- Kgs columns carry no `format`, so the returns pair needs the
                 same explicit formatting as the trio above. -->
            <template #cell-returned_kgs="{ value }">{{ formatNumber(value) }}</template>
            <template #cell-net_kgs="{ value }">{{ formatNumber(value) }}</template>
            <!-- C/O Shipping Cost / C/O Amount are the same net (received −
                 handed off) pattern as co_bottles/co_kgs_sold above, just in
                 currency — a biller who hands off most of their orders (e.g.
                 an accountant keying orders in on staff's behalf) shows a
                 large negative here, flagged in red the same way. -->
            <template #cell-co_shipping_amount="{ value }">
              <span :class="{ 'co-muted': !Number(value), 'co-neg': Number(value) < 0 }">{{ Number(value) ? formatCurrency(value) : '—' }}</span>
            </template>
            <template #cell-co_sales_ex_shipping="{ value }">
              <span :class="{ 'co-muted': !Number(value), 'co-neg': Number(value) < 0 }">{{ Number(value) ? formatCurrency(value) : '—' }}</span>
            </template>
            <template #cell-sales_amount="{ row, value }">
              <template v-if="Number(row && row.co_sales_amount) > 0">
                <div class="split-cell">
                  <div class="split-cell__value">{{ formatCurrency(value) }}</div>
                  <div class="split-cell__sub">{{ formatCurrency(row.co_sales_amount) }} c/o</div>
                </div>
              </template>
              <template v-else>{{ formatCurrency(value) }}</template>
            </template>
            <template #cell-return_amount="{ row, value }">
              <template v-if="Number(row && row.co_return_amount) > 0">
                <div class="split-cell">
                  <div class="split-cell__value">{{ formatCurrency(value) }}</div>
                  <div class="split-cell__sub">{{ formatCurrency(row.co_return_amount) }} c/o</div>
                </div>
              </template>
              <template v-else>{{ formatCurrency(value) }}</template>
            </template>
            <template #cell-total="{ row, value }">
              <template v-if="Number(row && row.co_total) > 0">
                <div class="split-cell">
                  <div class="split-cell__value">{{ formatNumber(value) }}</div>
                  <div class="split-cell__sub">{{ formatNumber(row.co_total) }} c/o</div>
                </div>
              </template>
              <template v-else>{{ formatNumber(value) }}</template>
            </template>
          </BaseTable>
        </div>
      </CCardBody>
    </CCard>

    <!-- Floating tile tooltip. Rendered at the page root and positioned
         with `position: fixed` so it escapes the tile card's overflow:hidden
         (which clips any in-card popover). -->
    <transition name="tile-tip-fade">
      <div
        v-if="infoTip.show"
        class="tile-tip"
        role="tooltip"
        :style="{ top: infoTip.y + 'px', left: infoTip.x + 'px' }"
      >{{ infoTip.text }}</div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import TelesalesDashboard from '@/views/TelesalesDashboard.vue'
import FieldSalesDashboard from '@/views/FieldSalesDashboard.vue'
import StorekeeperDashboard from '@/views/StorekeeperDashboard.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import { getStoredRoles } from '@/auth/access'
import { tileIcon as tileIconFor } from '@/config/dashboardTileIcons'

const ADMIN_TIER_ROLES = ['superadmin', 'admin', 'manager', 'operations', 'finance', 'ecom_manager', 'sales_supervisor']

// Tile keys that surface money figures. Hidden for roles whose dashboard
// view should omit price information (moderator, sales_supervisor,
// accounts_trainee). junior_accountant is NOT here — it sees the full admin
// dashboard; accounts_trainee is its clone but with monetary tiles stripped.
const MONETARY_TILE_KEYS = ['sales_amount', 'pending_payment', 'return_amount']
const MONETARY_HIDDEN_ROLES = ['moderator', 'sales_supervisor', 'accounts_trainee']

const TILE_ORDER = [
  'patients_visited',
  'bottles_sold_today',
  'farm_bottles_sold',
  'kgs_sold',
  'no_of_orders_today',
  'no_of_calls_today',
  'return_amount',
  'conversion_rate',
  'sales_amount',
  'pending_payment',
  'return_rate',
  'returns',
  'bottles_returned',
  'kgs_returned',
  'farm_bottles_returned',
  'total_bulk_orders',
  'bulk_bottles',
  'bulk_kgs',
  'bulk_farm_bottles'
  // Grade tiles disabled across all orgs.
  // 'grade_1',
  // 'grade_2',
  // 'grade_3',
  // 'grade_4',
  // 'grade_5'
]

const TILE_LABEL_OVERRIDES = {
  patients_visited: 'Patients Visited',
  bottles_sold_today: 'Bottles Sold',
  farm_bottles_sold: 'Farm Bottles Sold',
  no_of_orders_today: "Orders",
  conversion_rate: 'Total Conversion Rate',
  total_bulk_orders: 'Total Bulk Orders',
  bottles_returned: 'Bottles Returned',
  kgs_returned: 'Kgs Returned',
  farm_bottles_returned: 'Farm Bottles Returned',
  bulk_bottles: 'Bulk Bottles Sold',
  bulk_kgs: 'Bulk Kgs Sold',
  bulk_farm_bottles: 'Bulk Farm Bottles Sold'

}

// Tile icons now live in the shared map (@/config/dashboardTileIcons) so every
// dashboard renders the same glyph per metric. See `tileIcon` in methods.

// Tooltip copy surfaced via the small "i" badge on a tile's label.
const TILE_INFO = {
  patients_visited: 'OMS customers / total customers (HMS patients registered in the selected period)',
  no_of_calls_today: 'Telesales calls converted (linked to an order) / total calls in the selected period',
  conversion_rate: 'Total orders ÷ total calls in the selected period, as a percentage',
  bulk_bottles: 'Bottle units that earned a quantity discount in the selected period (excludes Agro Yield Super 1kg / farm bottles)',
  bulk_kgs: 'Agro Yield Super 1kg units that earned a quantity discount in the selected period',
  bulk_farm_bottles: 'Farm bottle units that earned a quantity discount in the selected period'
}

// Sales By Person has 26 columns on MAP — right for a CA auditing
// attribution, far too wide for the chairman / CEO / COO who read the same
// sheet. Summary keeps only the figures that report performance; everything
// dropped is a "Billed …" / "C/O …" column that exists to *prove* the
// attribution, or an intermediate the surrounding columns already imply.
//
// Detailed is the DEFAULT, deliberately: nothing disappears from anyone's
// existing workflow, and Summary is the opt-in. Bottles Sold is kept next to
// Bottles After Returns so the return impact is visible as a gap without
// needing the Return column itself.
const SALES_SUMMARY_KEYS = new Set([
  'user',
  'total_orders',
  'total_bottles',
  'net_bottles',
  'net_kgs',
  'net_farm_bottles',
  // Sold and after-returns money sit together, mirroring Bottles Sold next to
  // Bottles After Returns — the gap between the pair is the return impact.
  // Not org-conditional: all three orgs get both.
  'total_sales_ex_shipping',
  'net_amount_ex_shipping',
  'conversion'
])

const TILE_REPORT_MAP = {
  patients_visited: 'hms_patients',
  no_of_orders_today: 'sales',
  no_of_calls_today: 'roles',
  conversion_rate: 'roles',
  bottles_sold_today: 'item',
  kgs_sold: 'item',
  sales_amount: 'sales',
  pending_payment: 'sales',
  return_rate: 'item_return_report',
  returns: 'processed_returns',
  return_amount: 'processed_returns'
  // Grade tiles disabled across all orgs.
  // grade_1: 'item',
  // grade_2: 'item',
  // grade_3: 'item',
  // grade_4: 'item',
  // grade_5: 'item'
}

const HMS_ORGANISATION_ID = 2
const MANAKKAL_AGRO_ORGANISATION_ID = 1
const MANAKKAL_VET_ORGANISATION_ID = 4

// Returns-tab product filters. Available options and behaviour vary by
// organisation (see returnsFilterOptions / filteredProcessedReturns):
//   • org 1 (Agro): All / Except Agro Yield Super 1kg / Only Agro Yield Super 1kg
//   • org 2 (HMS):  All Products + one entry per distinct returned product
//   • org 4 (Vet):  All / Except Farm Bottles / Only Farm Bottles
//
// The predicate matchers run against a normalized name (lowercased, every
// non-alphanumeric char stripped) so separators don't defeat the match — e.g.
// "Agro Yield Super - 1 Kg" still counts as Agro Yield Super 1kg, and any name
// containing "farm" counts as a farm bottle (matching the backend's
// farm_bottles_returned split in orderController::combined).
const RETURNS_PRODUCT_MATCHERS = {
  ays1kg: (normalized) => ['agroyieldsuper', '1kg'].every((token) => normalized.includes(token)),
  farm: (normalized) => normalized.includes('farm')
}

export default {
  name: 'Dashboard',
  components: { TelesalesDashboard, FieldSalesDashboard, StorekeeperDashboard, AppDropdown },
  data () {
    const today = this.toDateInputValue(new Date())
    return {
      isLoading: false,
      errorMessage: '',
      lastUpdatedAt: null,
      filters: {
        fromDate: today,
        toDate: today
      },
      tiles: {},
      salesByPerson: [],
      itemwiseReport: [],
      itemReturnReport: [],
      processedReturnsReport: [],
      roleWiseReport: [],
      shippingReport: [],
      hmsPatientsReport: [],
      staffReturnReport: [],
      staffProductSales: { columns: [], rows: [] },
      districtSalesReport: [],
      districtProductSales: { columns: [], rows: [] },
      activeReport: 'item',
      returnsProductFilter: 'all',
      // Sales By Person search box + export-in-flight flag.
      spSearch: '',
      // 'summary' (default) | 'detailed' — drives the on-screen TABLE only.
      // The Excel export always ships the full detailed column set, whatever
      // this is set to: the screen is for whoever is glancing at it, the file
      // is for accounts/CA and must never arrive short of columns.
      spDetailLevel: 'summary',
      spExporting: false,
      // Staff Returns search box + export-in-flight flag.
      srSearch: '',
      srExporting: false,
      selectedTile: '',
      infoTip: { show: false, text: '', x: 0, y: 0 },
      tabScrollAtStart: true,
      tabScrollAtEnd: false
    }
  },
  computed: {
    isHmsOrganisation () {
      return Number(this.$store.state.activeOrganisationId) === HMS_ORGANISATION_ID
    },
    isManakkalAgroOrganisation () {
      return Number(this.$store.state.activeOrganisationId) === MANAKKAL_AGRO_ORGANISATION_ID
    },
    isManakkalVetOrganisation () {
      return Number(this.$store.state.activeOrganisationId) === MANAKKAL_VET_ORGANISATION_ID
    },
    // Full Sales By Person column set, in display order. Kept as its own
    // computed because the table and the export need DIFFERENT slices of it:
    // the table honours the Summary/Detailed toggle, the export never does.
    salesColumnsAll () {
      return [
        // Naming contract (applies to every quantity and money group
        // below, and to the Staff Returns tab):
        //   "Billed …"        gross — everything this person billed,
        //                     INCLUDING orders handed off via care_of
        //   "C/O …"           net care-of adjustment (received − handed
        //                     off); can be negative
        //   "… Sold"          Billed + C/O — what they are credited with
        //   "Return …"        returns handled in this period
        //   "… After Returns" Sold − Return
        // so the row reads left to right as the arithmetic:
        //   Billed + C/O = Sold;  Sold − Return = After Returns.
        // "Return …" deliberately carries the SAME name on both tabs, so
        // cross-checking is name-to-name with no translation step.
        // Every money column states whether it excludes shipping — a bare
        // "Amount" that silently dropped shipping is why the sheet would
        // not tie to invoices.
        { key: 'user', label: 'User' },
        { key: 'calls', label: 'Calls', align: 'right', format: 'number' },
        { key: 'order', label: 'Billed Orders', align: 'right', format: 'number' },
        { key: 'co_order', label: 'C/O Orders', align: 'right', format: 'number' },
        { key: 'total_orders', label: 'Orders', align: 'right', format: 'number' },
        { key: 'bottles', label: 'Billed Bottles', align: 'right', format: 'number' },
        // Present for HMS (org 2, no exclusion), Manakkal Agro (org 1,
        // bottles exclude Agro Yield Super 1kg) and Manakkal Vet (org 4,
        // bottles exclude farm bottles) — the exclusion is applied
        // server-side so these stay in sync with Billed Bottles.
        ...((this.isHmsOrganisation || this.isManakkalAgroOrganisation || this.isManakkalVetOrganisation) ? [
          { key: 'co_bottles', label: 'C/O Bottles', align: 'right', format: 'number' },
          { key: 'total_bottles', label: 'Bottles Sold', align: 'right', format: 'number' },
          { key: 'returned_bottles', label: 'Return Bottles', align: 'right', format: 'number' },
          { key: 'net_bottles', label: 'Bottles After Returns', align: 'right', format: 'number' }
        ] : []),
        // Manakkal Vet (org 4) farm group — counterpart to the kgs group.
        ...(this.isManakkalVetOrganisation ? [
          { key: 'farm_bottles', label: 'Billed Farm Bottles', align: 'right', format: 'number' },
          { key: 'co_farm_bottles', label: 'C/O Farm Bottles', align: 'right', format: 'number' },
          { key: 'total_farm_bottles', label: 'Farm Bottles Sold', align: 'right', format: 'number' },
          // Farm returns net against the farm column, never against bottles.
          { key: 'returned_farm_bottles', label: 'Return Farm Bottles', align: 'right', format: 'number' },
          { key: 'net_farm_bottles', label: 'Farm Bottles After Returns', align: 'right', format: 'number' }
        ] : []),
        // Kgs group is Agro Yield Super 1kg (item 220) only — Manakkal
        // Agro (org 1) exclusively.
        ...(this.isManakkalAgroOrganisation ? [
          { key: 'kgs_sold', label: 'Billed Kgs', align: 'right' },
          { key: 'co_kgs_sold', label: 'C/O Kgs', align: 'right' },
          { key: 'total_kgs_sold', label: 'Kgs Sold', align: 'right' },
          // Kg returns net against the KG column, never against bottles —
          // that swap was the larger half of the July 2026 discrepancy.
          { key: 'returned_kgs', label: 'Return Kgs', align: 'right' },
          { key: 'net_kgs', label: 'Kgs After Returns', align: 'right' }
        ] : []),
        // Money. Shipping is its own group and the Amount group is
        // entirely EXCLUDING shipping — every header says so, because a
        // bare "Amount" that silently dropped shipping is what made these
        // figures impossible to tie back to invoices. The Billed/C-O gap
        // is what flags a biller-role account (e.g. an accountant keying
        // orders in on staff's behalf) versus a real seller.
        { key: 'shipping_amount', label: 'Billed Shipping', align: 'right', format: 'currency' },
        { key: 'co_shipping_amount', label: 'C/O Shipping', align: 'right', format: 'currency' },
        { key: 'total_shipping_amount', label: 'Shipping', align: 'right', format: 'currency' },
        { key: 'returned_shipping_amount', label: 'Return Shipping', align: 'right', format: 'currency' },
        { key: 'net_shipping_amount', label: 'Shipping After Returns', align: 'right', format: 'currency' },
        { key: 'sales_ex_shipping', label: 'Billed Amount (Excl. Shipping)', align: 'right', format: 'currency' },
        { key: 'co_sales_ex_shipping', label: 'C/O Amount (Excl. Shipping)', align: 'right', format: 'currency' },
        { key: 'total_sales_ex_shipping', label: 'Amount Sold (Excl. Shipping)', align: 'right', format: 'currency' },
        { key: 'returned_amount_ex_shipping', label: 'Return Amount (Excl. Shipping)', align: 'right', format: 'currency' },
        { key: 'net_amount_ex_shipping', label: 'Amount After Returns (Excl. Shipping)', align: 'right', format: 'currency' },
        { key: 'conversion', label: 'Conversion', align: 'right' }
      ]
    },
    // What the on-screen table renders. Summary is the default here so the
    // page opens readable; Detailed is one click away. The EXPORT deliberately
    // ignores this and always ships salesColumnsAll — the downloaded file goes
    // to accounts/CA, who need every attribution column regardless of how the
    // screen happens to be set.
    salesTableColumns () {
      return this.salesColumnsAll.filter(
        (c) => this.spDetailLevel === 'detailed' || SALES_SUMMARY_KEYS.has(c.key)
      )
    },
    reportTabs () {
      const tabs = [
            {
          key: 'item',
          label: 'Itemwise Report',
          accent: 'linear-gradient(180deg, #0ea5e9, #0369a1)',
          items: this.itemwiseReport,
          emptyText: 'No itemwise records for the selected date range.'
        },
        {
          key: 'sales',
          label: 'Sales By Person',
          accent: 'var(--brand-gradient)',
          items: this.filteredSalesByPerson,
          columns: this.salesTableColumns,
          stickyFirstColumn: true,
          emptyText: 'No sales-by-person records for the selected date range.'
        },
        {
          key: 'staff_products',
          label: 'Staff Product Sales',
          accent: 'linear-gradient(180deg, #be185d, #db2777)',
          items: this.staffProductSales.rows,
          columns: [
            { key: 'user', label: 'Staff' },
            ...this.staffProductSales.columns.map((col) => ({
              key: col.key,
              label: col.label,
              align: 'right',
              format: 'number'
            })),
            { key: 'total', label: 'Total', align: 'right', format: 'number' }
          ],
          stickyFirstColumn: true,
          emptyText: 'No staff product sales for the selected date range.'
        },
    
        {
          key: 'item_return_report',
          label: 'Item Return Report',
          accent: 'linear-gradient(180deg, #c0392b, #e15543)',
          items: this.itemReturnReport,
          emptyText: 'No items sold in the selected date range.'
        },
        {
          // Per-order detail behind the Returns TILE. Scoped by return_date
          // (returns processed in the range) so the row count reconciles to
          // the tile — unlike Item Return Report, which is created_at cohort.
          key: 'processed_returns',
          label: 'Returns',
          accent: 'linear-gradient(180deg, #c0392b, #e15543)',
          items: this.filteredProcessedReturns,
          columns: [
            { key: 'sl_no', label: 'Sl. No', align: 'right', sortable: false },
            { key: 'order_number', label: 'Order #' },
            { key: 'customer', label: 'Customer' },
            { key: 'products', label: 'Products', sortable: false },
            { key: 'staff', label: 'Staff' },
            { key: 'return_date', label: 'Return Date', format: 'date' },
            { key: 'return_amount', label: 'Amount', align: 'right', format: 'currency' },
            { key: 'credit_note', label: 'Credit Note' }
          ],
          emptyText: 'No returns processed in the selected date range.'
        },
        {
          key: 'staff_return_report',
          label: 'Staff Returns',
          accent: 'linear-gradient(180deg, #c2410c, #f97316)',
          items: this.filteredStaffReturns,
          columns: [
            { key: 'user', label: 'Staff' },
            // "Return Bottles" / "Return Kgs" carry the SAME names as on Sales
            // By Person on purpose: those are the columns a CA cross-checks, so
            // the check is name-to-name. Both are own + c/o. The own-only and
            // c/o-only figures are kept alongside but explicitly labelled, so
            // nobody reconciles against a partial column by accident — that
            // ambiguity ("Kgs Returned" meaning own-only) was a live trap.
            // Own-billed only (care_of unset), unlike its Return Bottles /
            // Return Kgs siblings which are own + c/o. Labelled "Own …" so the
            // naming contract holds: an unprefixed "Return X" is always the
            // full own + c/o figure and is never double-counted by adding the
            // C/O column to it.
            { key: 'returns', label: 'Own Return Orders', align: 'right', format: 'number' },
            { key: 'co_orders_returned', label: 'C/O Return Orders', align: 'right', format: 'number' },
            { key: 'co_bottles_returned', label: 'C/O Return Bottles', align: 'right', format: 'number' },
            { key: 'total_bottles_returned', label: 'Return Bottles', align: 'right', format: 'number' },
            // Farm columns are products named "*farm*" only — Manakkal Vet (org 4);
            // backend excludes them from the bottle columns above.
            ...(this.isManakkalVetOrganisation ? [
              { key: 'co_farm_bottles_returned', label: 'C/O Return Farm Bottles', align: 'right', format: 'number' },
              { key: 'total_farm_bottles_returned', label: 'Return Farm Bottles', align: 'right', format: 'number' }
            ] : []),
            // Kgs columns are Agro Yield Super 1kg (item 220) only — Manakkal Agro (org 1).
            ...(this.isManakkalAgroOrganisation ? [
              { key: 'kgs_returned', label: 'Own Return Kgs', align: 'right', format: 'number' },
              { key: 'co_kgs_returned', label: 'C/O Return Kgs', align: 'right', format: 'number' },
              { key: 'total_kgs_returned', label: 'Return Kgs', align: 'right', format: 'number' }
            ] : []),
            { key: 'products', label: 'Products Returned', sortable: false },
            // Money. Return Shipping + Return Amount (Excl. Shipping) add back
            // up to Return Amount (Incl. Shipping) on every row — previously
            // two of these three used opposite shipping conventions and only
            // one said so, which made the sheet impossible to tie to invoices.
            // Amounts are own returns PLUS any return credited to this staffer
            // via c/o; row.co_return_amount is already on the row and the
            // shared cell-return_amount template surfaces it as a "₹X c/o"
            // subtext.
            { key: 'shipping_amount', label: 'Return Shipping', align: 'right', format: 'currency' },
            { key: 'return_ex_shipping', label: 'Return Amount (Excl. Shipping)', align: 'right', format: 'currency' },
            { key: 'return_amount', label: 'Return Amount (Incl. Shipping)', align: 'right', format: 'currency' }
          ],
          stickyFirstColumn: true,
          emptyText: 'No staff returns for the selected date range.'
        },
        {
          key: 'roles',
          label: 'Role-Wise Report',
          accent: 'linear-gradient(180deg, #0892a5, #1bb5c9)',
          items: this.roleWiseReport,
          columns: [
            { key: 'role', label: 'Role' },
            { key: 'orders', label: 'Orders', align: 'right', format: 'number' },
            { key: 'co_orders', label: 'C/O Orders', align: 'right', format: 'number' },
            { key: 'total_orders', label: 'Total Orders', align: 'right', format: 'number' },
            { key: 'bottles', label: 'Bottles', align: 'right', format: 'number' },
            { key: 'sales_amount', label: 'Sales Amount', align: 'right', format: 'currency' },
            { key: 'returns', label: 'Returns', align: 'right', format: 'number' },
            { key: 'return_rate', label: 'Return Rate', align: 'right', format: 'percent' },
            { key: 'return_amount', label: 'Return Amount', align: 'right', format: 'currency' }
          ],
          emptyText: 'No role activity for the selected date range.'
        },
        {
          key: 'shipping',
          label: 'Shipping Report',
          accent: 'linear-gradient(180deg, #2a2f8f, #3949ab)',
          items: this.shippingReport,
          emptyText: 'No shipping activity for the selected date range.'
        },
        {
          key: 'district_sales',
          label: 'Sales by District',
          accent: 'linear-gradient(180deg, #0e7490, #06b6d4)',
          items: this.districtSalesReport,
          columns: [
            { key: 'district', label: 'District' },
            { key: 'state', label: 'State' },
            { key: 'orders', label: 'Orders', align: 'right', format: 'number' },
            { key: 'bottles', label: 'Bottles', align: 'right', format: 'number' },
            { key: 'sales_amount', label: 'Sales Amount', align: 'right', format: 'currency' },
            { key: 'returns', label: 'Returns', align: 'right', format: 'number' },
            { key: 'return_rate', label: 'Return Rate', align: 'right', format: 'percent' },
            { key: 'return_amount', label: 'Return Amount', align: 'right', format: 'currency' }
          ],
          emptyText: 'No district sales for the selected date range.'
        },
        {
          key: 'district_products',
          label: 'Product Sales by District',
          accent: 'linear-gradient(180deg, #7c3aed, #a78bfa)',
          items: this.districtProductSales.rows,
          columns: [
            { key: 'district', label: 'District' },
            ...this.districtProductSales.columns.map((col) => ({
              key: col.key,
              label: col.label,
              align: 'right',
              format: 'number'
            })),
            { key: 'total', label: 'Total', align: 'right', format: 'number' }
          ],
          stickyFirstColumn: true,
          emptyText: 'No product sales by district for the selected date range.'
        }
      ]
      if (this.isHmsOrganisation) {
        tabs.push({
          key: 'hms_patients',
          label: 'HMS Patients',
          accent: 'linear-gradient(180deg, #6a1b9a, #8e24aa)',
          items: this.hmsPatientsReport,
          columns: [
            { key: 'user', label: 'User' },
            { key: 'patients', label: 'Patients', align: 'right' },
            { key: 'oms_purchased', label: 'OMS Purchased', align: 'right' },
            { key: 'needs_review', label: 'Needs Review', align: 'right' },
            { key: 'conversion', label: 'Conversion', align: 'right', format: 'percent' }
          ],
          emptyText: 'No patient registrations for the selected date range.'
        })
      }
      return tabs
    },
    activeTab () {
      return this.reportTabs.find((tab) => tab.key === this.activeReport) || this.reportTabs[0]
    },
    // Whether the Returns tab shows a product filter for the active org.
    returnsFilterActive () {
      return this.isManakkalAgroOrganisation || this.isManakkalVetOrganisation || this.isHmsOrganisation
    },
    // Distinct returned-product names (org 2 / HMS lists every product so a
    // user can isolate returns of any single product). Deduped case/separator-
    // insensitively, keeping the first-seen display spelling, sorted A→Z.
    returnsProductNames () {
      const seen = new Map()
      for (const row of this.processedReturnsReport) {
        const products = Array.isArray(row.products) ? row.products : []
        for (const prod of products) {
          const name = prod && prod.name
          if (!name) continue
          const key = this.normalizeProductName(name)
          if (key && !seen.has(key)) seen.set(key, String(name))
        }
      }
      return Array.from(seen.values()).sort((a, b) => a.localeCompare(b))
    },
    // Org-specific Returns filter options. Predicate filters (Agro/Vet) encode
    // the mode + matcher key as `except:<key>` / `only:<key>`; HMS encodes a
    // single product as `product:<name>`. 'all' is the no-op default.
    returnsFilterOptions () {
      if (this.isManakkalAgroOrganisation) {
        return [
          { value: 'all', label: 'All Returns' },
          { value: 'except:ays1kg', label: 'Except Agro Yield Super 1kg' },
          { value: 'only:ays1kg', label: 'Only Agro Yield Super 1kg' }
        ]
      }
      if (this.isManakkalVetOrganisation) {
        return [
          { value: 'all', label: 'All Returns' },
          { value: 'except:farm', label: 'Except Farm Bottles' },
          { value: 'only:farm', label: 'Only Farm Bottles' }
        ]
      }
      if (this.isHmsOrganisation) {
        return [
          { value: 'all', label: 'All Products' },
          ...this.returnsProductNames.map((name) => ({ value: `product:${name}`, label: name }))
        ]
      }
      return []
    },
    // Returns tab rows after the active product filter. 'except' strips the
    // matching product lines (dropping rows left empty); 'only' / 'product'
    // keeps just the matching lines. The row's return_amount is order-level and
    // is left untouched — these filters scope the visible bottles, not the value.
    filteredProcessedReturns () {
      const rows = this.processedReturnsReport
      if (!this.returnsFilterActive) return rows
      const sel = this.returnsProductFilter
      // Guard against a stale cross-org selection (org switch keeps the value
      // until the page remounts): only honour values valid for this org.
      const valid = new Set(this.returnsFilterOptions.map((opt) => opt.value))
      if (!sel || sel === 'all' || !valid.has(sel)) return rows

      const [mode, key] = sel.split(/:(.+)/)
      let matches
      if (mode === 'product') {
        const target = this.normalizeProductName(key)
        matches = (name) => this.normalizeProductName(name) === target
      } else {
        const matcher = RETURNS_PRODUCT_MATCHERS[key]
        if (!matcher) return rows
        matches = (name) => matcher(this.normalizeProductName(name))
      }
      const keepOnly = mode === 'only' || mode === 'product'

      const out = []
      for (const row of rows) {
        const products = Array.isArray(row.products) ? row.products : []
        const kept = keepOnly
          ? products.filter((prod) => matches(prod && prod.name))
          : products.filter((prod) => !matches(prod && prod.name))
        if (kept.length) out.push({ ...row, products: kept })
      }
      return out
    },
    // Sales By Person rows after the search box (matches the staff name).
    filteredSalesByPerson () {
      const q = this.spSearch.trim().toLowerCase()
      if (!q) return this.salesByPerson
      return this.salesByPerson.filter((row) => String(row.user || '').toLowerCase().includes(q))
    },
    // Staff Returns rows after the search box (matches the staff name).
    filteredStaffReturns () {
      const q = this.srSearch.trim().toLowerCase()
      if (!q) return this.staffReturnReport
      return this.staffReturnReport.filter((row) => String(row.user || '').toLowerCase().includes(q))
    },
    tileCards () {
      const knownKeys = TILE_ORDER.filter((key) => Object.prototype.hasOwnProperty.call(this.tiles, key))
      const otherKeys = Object.keys(this.tiles).filter((key) => !knownKeys.includes(key))
      const cards = [...knownKeys, ...otherKeys]
        // patients_oms is the OMS-customer subset rendered inside the
        // patients_visited tile ("oms / total"), never its own tile.
        .filter((key) => key !== 'patients_oms')
        // calls_converted is the converted subset rendered inside the
        // no_of_calls_today tile ("converted / total"), never its own tile.
        .filter((key) => key !== 'calls_converted')
        // Grade tiles disabled across all orgs (backend still sends them).
        .filter((key) => !/^grade_\d+$/.test(key))
        .filter((key) => key !== 'patients_visited' || this.isHmsOrganisation)
        .filter((key) => key !== 'kgs_sold' || this.isManakkalAgroOrganisation)
        // Bulk discount split: Bulk Bottles Sold is shown for Manakkal Agro
        // (org 1, paired with Bulk Kgs Sold), Manakkal Vet (org 4, paired with
        // Bulk Farm Bottles Sold) and Manakkal Drugs (org 2, bottles-only — the
        // single tile). Bulk Kgs Sold is org 1 only; Bulk Farm Bottles Sold is
        // org 4 only.
        .filter((key) => key !== 'bulk_bottles' || this.isManakkalAgroOrganisation || this.isManakkalVetOrganisation || this.isHmsOrganisation)
        .filter((key) => key !== 'bulk_kgs' || this.isManakkalAgroOrganisation)
        .filter((key) => key !== 'bulk_farm_bottles' || this.isManakkalVetOrganisation)
        // Roles that should see the admin dashboard without monetary tiles
        // (moderator, sales_supervisor) get the price-bearing tiles stripped.
        .filter((key) => !this.hideMonetaryTiles || !MONETARY_TILE_KEYS.includes(key))
        .map((key) => ({
          key,
          label: TILE_LABEL_OVERRIDES[key] || this.formatLabel(key),
          value: this.tiles[key]
        }))
      // Reposition a tile to a fixed slot (1-based position → 0-based index).
      const moveTo = (key, position) => {
        const idx = cards.findIndex((card) => card.key === key)
        if (idx === -1) return
        const [card] = cards.splice(idx, 1)
        cards.splice(Math.min(position, cards.length), 0, card)
      }
      // Manakkal Agro wants Sales Amount as the 5th tile and Pending
      // Payment as the 6th.
      if (this.isManakkalAgroOrganisation) {
        moveTo('sales_amount', 4)
        moveTo('pending_payment', 5)
      }
      // HMS wants Patients Visited as the 7th tile.
      if (this.isHmsOrganisation) {
        moveTo('patients_visited', 6)
      }
      // Total Bulk Orders is pinned to the 9th tile (0-based index 8).
      moveTo('total_bulk_orders', 8)
      // Manakkal Vet wants Return Amount as the 7th tile.
      if (this.isManakkalVetOrganisation) {
        moveTo('return_amount', 6)
      }
      // Total Conversion Rate is pinned to the 8th tile (0-based index 7).
      moveTo('conversion_rate', 7)
      // Manakkal Agro (org 1): Bulk Bottles and Bulk Kgs sit at the 9th and
      // 10th slots, with Total Bulk Orders pushed to the 11th so the three
      // bulk/discount tiles stay grouped.
      if (this.isManakkalAgroOrganisation) {
        moveTo('bulk_bottles', 8)
        moveTo('bulk_kgs', 9)
        moveTo('total_bulk_orders', 10)
      }
      // Manakkal Vet (org 4): Bulk Bottles and Bulk Farm Bottles at the 9th and
      // 10th slots, Total Bulk Orders at the 11th — same grouping as org 1.
      if (this.isManakkalVetOrganisation) {
        moveTo('bulk_bottles', 8)
        moveTo('bulk_farm_bottles', 9)
        moveTo('total_bulk_orders', 10)
      }
      // Manakkal Drugs (org 2, HMS): bottles-only — a single Bulk Bottles Sold
      // tile at the 9th slot, with Total Bulk Orders right after it.
      if (this.isHmsOrganisation) {
        moveTo('bulk_bottles', 8)
        moveTo('total_bulk_orders', 9)
      }
      return cards
    },
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return 'Not fetched yet'
      return this.lastUpdatedAt.toLocaleString()
    },
    isTelesalesStaff () {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      if (!roles.includes('telesales')) return false
      return !roles.some((r) => ADMIN_TIER_ROLES.includes(r))
    },
    isFieldStaff () {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      if (!roles.includes('field_staff')) return false
      return !roles.some((r) => ADMIN_TIER_ROLES.includes(r))
    },
    isStorekeeperStaff () {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      if (!roles.includes('storekeeper')) return false
      return !roles.some((r) => ADMIN_TIER_ROLES.includes(r))
    },
    hideMonetaryTiles () {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      return roles.some((r) => MONETARY_HIDDEN_ROLES.includes(r))
    },
    // Rough tile count for the initial-load skeleton. Picked to fill
    // two rows on xl (4 per row) so the page doesn't visibly shift
    // when the real tile set arrives.
    skeletonTileCount () {
      return this.isHmsOrganisation ? 12 : 8
    }
  },
  created () {
    if (this.isTelesalesStaff || this.isFieldStaff || this.isStorekeeperStaff) return
    this.fetchDashboardData()
  },
  mounted () {
    this.$nextTick(this.updateTabScroll)
    window.addEventListener('resize', this.updateTabScroll)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateTabScroll)
  },
  methods: {
    // Update the left/right tab-scroll arrows based on the strip's scroll
    // position. Each arrow hides once that end is reached.
    updateTabScroll () {
      const el = this.$refs.reportTabs
      if (!el) return
      const max = el.scrollWidth - el.clientWidth
      this.tabScrollAtStart = el.scrollLeft <= 1
      // 1px slack so sub-pixel widths don't leave the arrow stuck visible.
      this.tabScrollAtEnd = el.scrollLeft >= max - 1
    },
    scrollReportTabs (dir) {
      const el = this.$refs.reportTabs
      if (!el) return
      // Scroll roughly 70% of the visible width per click.
      el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.7), behavior: 'smooth' })
    },
    toDateInputValue (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    getPayload () {
      return {
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate
      }
    },
    applyFilters () {
      this.fetchDashboardData()
    },
    reloadData () {
      this.fetchDashboardData()
    },
    tileIcon (key) {
      return tileIconFor(key)
    },
    tileInfo (key) {
      return TILE_INFO[key] || ''
    },
    showTileInfo (event, key) {
      const text = this.tileInfo(key)
      if (!text) return
      const rect = event.currentTarget.getBoundingClientRect()
      this.infoTip = {
        show: true,
        text,
        // Centred above the badge; the tooltip's own transform anchors it.
        x: rect.left + rect.width / 2,
        y: rect.top - 8
      }
    },
    hideTileInfo () {
      this.infoTip.show = false
    },
    onTileClick (key) {
      this.selectedTile = this.selectedTile === key ? '' : key
      const targetTab = TILE_REPORT_MAP[key]
      if (targetTab && this.reportTabs.some((tab) => tab.key === targetTab)) {
        this.activeReport = targetTab
      }
    },
    tileCardClass (key, index) {
      const toneMap = {
        patients_visited: 'tile-violet',
        bottles_sold_today: 'tile-slate',
        farm_bottles_sold: 'tile-forest',
        kgs_sold: 'tile-cyan',
        conversion_rate: 'tile-amber',
        no_of_calls_today: 'tile-emerald',
        pending_payment: 'tile-orange',
        sales_amount: 'tile-teal',
        return_rate: 'tile-plum',
        returns: 'tile-crimson',
        return_amount: 'tile-sky',
        bottles_returned: 'tile-coral',
        kgs_returned: 'tile-gold',
        farm_bottles_returned: 'tile-bronze',
        total_bulk_orders: 'tile-rose',
        bulk_bottles: 'tile-slate',
        bulk_kgs: 'tile-cyan',
        bulk_farm_bottles: 'tile-forest',
        no_of_orders_today: 'tile-indigo'
        // Grade tiles disabled across all orgs.
        // grade_1: 'tile-sky',
        // grade_2: 'tile-fuchsia',
        // grade_3: 'tile-ocean',
        // grade_4: 'tile-lime',
        // grade_5: 'tile-purple'
      }

      const fallbackTones = ['tile-indigo', 'tile-ocean', 'tile-emerald', 'tile-amber', 'tile-slate', 'tile-crimson']
      return toneMap[key] || fallbackTones[index % fallbackTones.length]
    },
    async fetchDashboardData () {
      if (!this.filters.fromDate || !this.filters.toDate) {
        this.errorMessage = 'Please select both From Date and To Date.'
        return
      }

      if (this.filters.fromDate > this.filters.toDate) {
        this.errorMessage = 'From Date must be before or equal to To Date.'
        return
      }

      this.isLoading = true
      this.errorMessage = ''

      try {
        const payload = this.getPayload()
        // Combined endpoint — one authed request instead of two parallel
        // POSTs. Halves Sanctum token lookups and DB connection acquires
        // on every dashboard load.
        const response = await axios.post(`${this.$apiAdress}/api/dashboard/combined`, payload)
        const combined = response.data && typeof response.data === 'object' ? response.data : {}

        this.tiles = this.normalizeTiles(combined.tiles)
        const dashboardData = this.normalizeDashboard(combined.dashboard)
        // Decorate each biller row with total_orders (own + c/o) so the
        // table can show the combined count the biller actually placed.
        // total_orders / total_bottles / total_kgs_sold are own + c/o (the
        // count that actually counts as the staffer's), mirroring the
        // order + co_order pattern. kgs fields are item 220 (Agro Yield
        // Super 1kg) only and present for org 1; they fall back to 0 elsewhere.
        this.salesByPerson = (Array.isArray(dashboardData.sales_by_person) ? dashboardData.sales_by_person : [])
          .map((row) => ({
            ...row,
            total_orders: Number(row.order || 0) + Number(row.co_order || 0),
            total_bottles: Number(row.bottles || 0) + Number(row.co_bottles || 0),
            total_kgs_sold: Number(row.kgs_sold || 0) + Number(row.co_kgs_sold || 0),
            total_farm_bottles: Number(row.farm_bottles || 0) + Number(row.co_farm_bottles || 0),
            total_shipping_amount: Number(row.shipping_amount || 0) + Number(row.co_shipping_amount || 0),
            total_sales_ex_shipping: Number(row.sales_ex_shipping || 0) + Number(row.co_sales_ex_shipping || 0)
          }))
        this.itemwiseReport = Array.isArray(dashboardData.itemwise_report) ? dashboardData.itemwise_report : []
        this.itemReturnReport = Array.isArray(dashboardData.item_return_report) ? dashboardData.item_return_report : []
        this.processedReturnsReport = Array.isArray(dashboardData.processed_returns_report) ? dashboardData.processed_returns_report : []
        this.roleWiseReport = Array.isArray(dashboardData.role_wise_report) ? dashboardData.role_wise_report : []
        this.shippingReport = Array.isArray(dashboardData.shipping_report) ? dashboardData.shipping_report : []
        this.hmsPatientsReport = Array.isArray(dashboardData.hms_patients_report) ? dashboardData.hms_patients_report : []
        this.staffReturnReport = Array.isArray(dashboardData.staff_return_report) ? dashboardData.staff_return_report : []
        const sps = dashboardData.staff_product_sales
        this.staffProductSales = sps && typeof sps === 'object'
          ? {
            columns: Array.isArray(sps.columns) ? sps.columns : [],
            rows: Array.isArray(sps.rows) ? sps.rows : []
          }
          : { columns: [], rows: [] }
        this.districtSalesReport = Array.isArray(dashboardData.district_sales_report) ? dashboardData.district_sales_report : []
        const dps = dashboardData.district_product_sales
        this.districtProductSales = dps && typeof dps === 'object'
          ? {
            columns: Array.isArray(dps.columns) ? dps.columns : [],
            rows: Array.isArray(dps.rows) ? dps.rows : []
          }
          : { columns: [], rows: [] }
        if (this.activeReport === 'hms_patients' && !this.isHmsOrganisation) {
          this.activeReport = 'sales'
        }
        this.lastUpdatedAt = new Date()
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Unable to load dashboard data right now.'
      } finally {
        this.isLoading = false
        // Tab counts appear once data loads, which can change the strip's
        // scroll width — refresh the arrow visibility after the DOM updates.
        this.$nextTick(this.updateTabScroll)
      }
    },
    normalizeTiles (payload) {
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        return payload
      }
      return {}
    },
    normalizeDashboard (payload) {
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        return payload
      }
      return {}
    },
    normalizeProductName (name) {
      // Lowercase and drop every non-alphanumeric char so separators
      // (spaces, hyphens, parentheses) don't defeat the token match.
      return String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
    },
    formatLabel (key) {
      return String(key || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    },
    // Conversion is stored as a display string ("50 %" / "N/A"). Pull the
    // leading number out for filtering + export; null when not a number.
    parseConversion (value) {
      if (value === null || value === undefined) return null
      const match = String(value).match(/-?\d+(\.\d+)?/)
      return match ? Number(match[0]) : null
    },
    // Excel type for a Sales By Person column — drives number formatting in
    // the workbook (counts vs kg decimals vs the conversion percentage).
    salesColumnType (key) {
      if (key === 'user') return 'text'
      if (key === 'conversion') return 'percent'
      if ([
        'kgs_sold', 'co_kgs_sold', 'total_kgs_sold', 'returned_kgs', 'net_kgs',
        'shipping_amount', 'co_shipping_amount', 'total_shipping_amount',
        'sales_ex_shipping', 'co_sales_ex_shipping', 'total_sales_ex_shipping',
        'returned_amount_ex_shipping', 'net_amount_ex_shipping',
        'returned_shipping_amount', 'net_shipping_amount'
      ].includes(key)) return 'decimal'
      return 'number'
    },
    salesExportValue (key, row) {
      if (key === 'user') return row.user || ''
      if (key === 'conversion') return this.parseConversion(row.conversion)
      return row[key]
    },
    // Export the *currently visible* Sales By Person rows (post search +
    // filters) to a styled .xlsx. We send the columns + raw values; the
    // backend (SalesByPersonExport) owns all presentation.
    async exportSalesByPerson () {
      if (this.spExporting) return
      // salesColumnsAll, NOT the tab's columns: the table may be showing the
      // Summary slice, but the downloaded file must always carry the full
      // detailed set for accounts/CA. Rows are still the *visible* rows, so
      // the search box continues to scope the export.
      const cols = this.salesColumnsAll
      if (!cols.length || !this.filteredSalesByPerson.length) return

      const columns = cols.map((c) => ({ label: c.label, type: this.salesColumnType(c.key) }))
      const rows = this.filteredSalesByPerson.map((row) => cols.map((c) => this.salesExportValue(c.key, row)))
      const orgName = this.$store.state.activeOrganisationName
      const range = `${this.filters.fromDate} to ${this.filters.toDate}`
      // The returns columns are scoped by return_date (returns HANDLED in this
      // period) while the sales columns are scoped by created_at (orders PLACED
      // in it) — so a return here can belong to an order from an earlier month.
      // Spell that out on the sheet: without it a reader assumes the returned
      // figure is a subset of the bottles figure, which is what caused the
      // July 2026 query from accounts.
      const subtitle = `${orgName ? orgName + '  ·  ' : ''}${range}  ·  ${rows.length} staff`
        + '\nReturned bottles are returns handled during this period'

      this.spExporting = true
      try {
        const response = await axios.post(
          `${this.$apiAdress}/api/dashboard/sales-by-person-xlsx`,
          { title: 'Sales By Person', subtitle, columns, rows },
          { responseType: 'blob' }
        )
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `sales_by_person_${this.filters.fromDate}_to_${this.filters.toDate}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.errorMessage = 'Unable to export Sales By Person right now.'
      } finally {
        this.spExporting = false
      }
    },
    // Excel type for a Staff Returns column — products is a free-text cell,
    // the money columns carry currency, everything else is a plain count.
    staffReturnColumnType (key) {
      if (key === 'user' || key === 'products') return 'text'
      if (['return_amount', 'shipping_amount', 'return_ex_shipping'].includes(key)) return 'decimal'
      return 'number'
    },
    staffReturnExportValue (key, row) {
      if (key === 'user') return row.user || ''
      if (key === 'products') {
        const list = Array.isArray(row.products) ? row.products : []
        return list.map((p) => `${p.name} ×${p.quantity}`).join(', ')
      }
      return row[key]
    },
    // Export the *currently visible* Staff Returns rows (post search) to a
    // styled .xlsx, reusing the generic Sales-By-Person workbook builder.
    async exportStaffReturns () {
      if (this.srExporting) return
      const tab = this.reportTabs.find((t) => t.key === 'staff_return_report')
      const cols = (tab && tab.columns) || []
      if (!cols.length || !this.filteredStaffReturns.length) return

      const columns = cols.map((c) => ({ label: c.label, type: this.staffReturnColumnType(c.key) }))
      const rows = this.filteredStaffReturns.map((row) => cols.map((c) => this.staffReturnExportValue(c.key, row)))
      const orgName = this.$store.state.activeOrganisationName
      const range = `${this.filters.fromDate} to ${this.filters.toDate}`
      // Same period caveat as the Sales By Person export: this table is scoped
      // by return_date, so a row can belong to an order placed earlier.
      const subtitle = `${orgName ? orgName + '  ·  ' : ''}${range}  ·  ${rows.length} staff`
        + '\nReturns handled during this period — the order may have been placed earlier'

      this.srExporting = true
      try {
        const response = await axios.post(
          `${this.$apiAdress}/api/dashboard/sales-by-person-xlsx`,
          { title: 'Staff Returns', subtitle, columns, rows },
          { responseType: 'blob' }
        )
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `staff_returns_${this.filters.fromDate}_to_${this.filters.toDate}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.errorMessage = 'Unable to export Staff Returns right now.'
      } finally {
        this.srExporting = false
      }
    },
    formatNumber (value) {
      if (value === null || value === undefined || value === '') return '—'
      const numeric = Number(value)
      if (Number.isNaN(numeric)) return value
      return new Intl.NumberFormat('en-IN').format(numeric)
    },
    formatCurrency (value) {
      if (value === null || value === undefined || value === '') return '—'
      const numeric = Number(value)
      if (Number.isNaN(numeric)) return value
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2
      }).format(numeric)
    },
    formatTileValue (key, value) {
      if (value === null || value === undefined || value === '') return 'N/A'
      // Patients Visited reads "OMS customers / total customers".
      if (key === 'patients_visited') {
        const total = Number(value) || 0
        const oms = Number(this.tiles.patients_oms) || 0
        return `${this.formatNumber(oms)} / ${this.formatNumber(total)}`
      }
      // Calls tile reads "calls converted / total calls".
      if (key === 'no_of_calls_today') {
        const total = Number(value) || 0
        const converted = Number(this.tiles.calls_converted) || 0
        return `${this.formatNumber(converted)} / ${this.formatNumber(total)}`
      }
      if (['sales_amount', 'pending_payment', 'return_amount'].includes(key)) {
        const numeric = Number(value)
        if (Number.isNaN(numeric)) return value
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          maximumFractionDigits: 2
        }).format(numeric)
      }
      if (['conversion_rate', 'return_rate'].includes(key)) return `${value} %`
      if (['kgs_sold', 'kgs_returned'].includes(key)) {
        const numeric = Number(value)
        if (Number.isNaN(numeric)) return value
        return `${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(numeric)} kg`
      }
      return value
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  border-radius: 14px;
  background: linear-gradient(180deg, #f4f7fb 0%, #eef3fa 100%);
}

.dashboard-card {
  border-radius: 14px;
}

.tile-col {
  height: 95px;
}

.filter-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* Tighten Bootstrap row gutters (default is 30px / 15px each side) */
.tight-row {
  margin-left: -6px;
  margin-right: -6px;
  row-gap: 0.4rem;
}

.tight-row > [class*='col-'],
.tight-row > .col {
  padding-left: 6px;
  padding-right: 6px;
}

/* Reports tabs */
.reports-section {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

/* Tab strip + flanking scroll arrows */
.report-tabs-wrap {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: fit-content;
  max-width: 100%;
}

.report-tabs {
  display: flex;
  gap: 0.25rem;
  flex-wrap: nowrap;
  padding: 0.25rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.report-tabs::-webkit-scrollbar {
  display: none;
}

/* Round scroll-arrow buttons at each end of the tab strip. */
.tab-scroll-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #475569;
  padding: 0;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}

.tab-scroll-btn svg {
  display: block;
}

.tab-scroll-btn:hover {
  background: #f1f5f9;
  color: #0e5b46;
  border-color: #cdd6e4;
}

.tab-scroll-btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* When that end is reached the arrow fades out (kept in flow so the strip
   doesn't shift). */
.tab-scroll-btn.is-hidden {
  opacity: 0.3;
  pointer-events: none;
}

.report-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.1rem;
  border: none;
  outline: none;
  background: transparent;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  -webkit-tap-highlight-color: transparent;
  flex: 0 0 auto;
}

.report-tab:focus {
  outline: none;
  box-shadow: none;
}

.report-tab:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.25);
}

.report-tab:hover:not(.is-active) {
  background: #f1f5f9;
  color: #334155;
}

.report-tab.is-active {
  background: var(--brand-gradient);
  color: #ffffff;
  box-shadow: var(--brand-shadow);
}

.tab-count {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  background: rgba(14, 91, 70, 0.10);
  color: var(--brand-color);
}

.report-tab.is-active .tab-count {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

/* Returns tab — Agro Yield Super 1kg quick filter */
.returns-filter {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
}

.returns-filter__label {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
}

.returns-filter__select {
  min-width: 240px;
}

/* Sales By Person — search + filters + export toolbar */
.sbp-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 0.85rem;
}

.sbp-search {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 200px;
}

.sbp-search svg {
  position: absolute;
  left: 0.65rem;
  color: #94a3b8;
  pointer-events: none;
}

.sbp-search__input {
  width: 100%;
  height: 36px;
  padding: 0 0.75rem 0 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #1f2a44;
  background: #ffffff;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.sbp-search__input::placeholder { color: #9aa3b2; }

.sbp-search__input:focus-visible {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

/* Detail-level switch. Segmented pill matching the page tab-bar contract:
   999px container, brand gradient on the active segment. It takes the
   margin-left:auto so it and the export button sit together on the right —
   without the override below, both would claim the free space and split it. */
.sbp-level {
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  padding: 2px;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.sbp-level__btn {
  height: 30px;
  padding: 0 0.85rem;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: #64748b;
  font-size: 0.79rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.sbp-level__btn::-moz-focus-inner { border: 0; }

.sbp-level__btn:hover:not(.is-active) {
  background: #f1f5f9;
  color: #334155;
}

.sbp-level__btn.is-active {
  background: var(--brand-gradient);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28);
}

.sbp-level__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.sbp-level + .sbp-export {
  margin-left: 0.5rem;
}

.sbp-export {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 36px;
  margin-left: auto;
  padding: 0 1.05rem;
  border: none;
  border-radius: 8px;
  background: var(--brand-gradient);
  color: #ffffff;
  font-size: 0.83rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.sbp-export:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.24);
}

.sbp-export:focus-visible {
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.4);
}

.sbp-export:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 575px) {
  .sbp-search { max-width: 100%; }
  .sbp-export { margin-left: 0; width: 100%; justify-content: center; }
}

.filter-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  height: 40px;
  min-width: 110px;
  padding: 0 1.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-radius: 999px;
}

.reload-icon-btn {
  min-width: 40px;
  width: 40px;
  padding: 0;
  background: #ffffff;
}

.reload-glyph {
  font-size: 1.1rem;
  line-height: 1;
  font-weight: 700;
  color: #475569;
}

.tile-card {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.10);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  padding: 0;
  text-align: left;
  cursor: pointer;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}

.tile-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0) 55%);
  pointer-events: none;
  transition: background 0.2s ease;
}

.tile-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.18);
}

.tile-card:hover .tile-icon {
  transform: scale(1.08) rotate(-4deg);
  background: rgba(255, 255, 255, 0.28);
}

.tile-card:active {
  transform: translateY(-1px) scale(0.99);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.16);
}

.tile-card:focus-visible {
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.45), 0 14px 28px rgba(15, 23, 42, 0.18);
}

.tile-card.is-selected {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.85) inset, 0 14px 30px rgba(15, 23, 42, 0.22);
  transform: translateY(-2px);
}

.tile-card.is-selected::after {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.32), rgba(255, 255, 255, 0.04) 60%);
}

.tile-card.is-selected .tile-icon {
  background: rgba(255, 255, 255, 0.32);
  transform: scale(1.05);
}

.tile-body {
  padding: 1.1rem 1.2rem;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  height: 100%;
}

.tile-text {
  min-width: 0;
  flex: 1 1 auto;
}

.tile-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  /* background: rgba(255, 255, 255, 0.18); */
  color: #ffffff;
  flex: 0 0 auto;
  transition: transform 0.2s ease, background 0.2s ease;
}

.tile-icon .c-icon {
  width: 20px;
  height: 20px;
  color: #ffffff;
}

.tile-value {
  font-size: 1.3rem;
  /* 700 not 800: Windows (Segoe UI + DirectWrite) renders heavy weights
     noticeably thicker than macOS (San Francisco), so 800 that looks
     confident on Mac reads as over-bold on Windows — worst on light
     text over a saturated tile. 700 closes most of that gap. */
  font-weight: 700;
  line-height: 1.1;
  color: #ffffff;
  letter-spacing: -0.01em;
  margin-bottom: 0.35rem;
}

.tile-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.01em;
}

/* Small "i" badge sitting after a tile label. Carries a native title
   tooltip — the tile card clips overflow (for the gradient sheen), so a
   CSS popover would be cut off; the native tooltip escapes the clip. */
.tile-info {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-left: 0.3rem;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.65);
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
  font-size: 0.58rem;
  font-weight: 700;
  font-style: normal;
  line-height: 1;
  vertical-align: middle;
  cursor: help;
  outline: none;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.tile-info:hover {
  background: rgba(255, 255, 255, 0.28);
  border-color: rgba(255, 255, 255, 0.9);
}

.tile-info:focus-visible {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.6);
}

/* Floating tooltip (position: fixed, rendered at page root) — escapes the
   tile card's overflow:hidden so it can sit above the badge. */
.tile-tip {
  position: fixed;
  z-index: 2000;
  transform: translate(-50%, -100%);
  max-width: 240px;
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  background: #1f2a44;
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 500;
  line-height: 1.35;
  text-align: left;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.28);
  pointer-events: none;
}

.tile-tip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #1f2a44;
}

.tile-tip-fade-enter-active,
.tile-tip-fade-leave-active {
  transition: opacity 0.15s ease;
}

.tile-tip-fade-enter,
.tile-tip-fade-leave-to {
  opacity: 0;
}


.tile-slate {
  background: linear-gradient(135deg, #1f2a44 0%, #34405e 100%);
}

.tile-indigo {
  background: linear-gradient(135deg, #2a2f8f 0%, #3949ab 100%);
}

.tile-ocean {
  background: linear-gradient(135deg, #2f80c8 0%, #4ea3e0 100%);
}

.tile-cyan {
  background: linear-gradient(135deg, #0892a5 0%, #1bb5c9 100%);
}

.tile-violet {
  background: linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%);
}

.tile-emerald {
  background: linear-gradient(135deg, #2e9d5f 0%, #43b97a 100%);
}

.tile-amber {
  background: linear-gradient(135deg, #e58e1a 0%, #f1ab3c 100%);
}

.tile-rose {
  background: linear-gradient(135deg, #c4495b 0%, #d96374 100%);
}

.tile-crimson {
  background: linear-gradient(135deg, #c0392b 0%, #e15543 100%);
}

.tile-teal {
  background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%);
}

.tile-orange {
  background: linear-gradient(135deg, #c2410c 0%, #f97316 100%);
}

.tile-sky {
  background: linear-gradient(135deg, #0284c7 0%, #38bdf8 100%);
}

.tile-fuchsia {
  background: linear-gradient(135deg, #a21caf 0%, #d946ef 100%);
}

.tile-lime {
  background: linear-gradient(135deg, #4d7c0f 0%, #84cc16 100%);
}

.tile-purple {
  background: linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%);
}

.tile-plum {
  background: linear-gradient(135deg, #831843 0%, #be185d 100%);
}

.tile-coral {
  background: linear-gradient(135deg, #e11d48 0%, #fb7185 100%);
}

.tile-gold {
  background: linear-gradient(135deg, #a16207 0%, #eab308 100%);
}

.tile-bronze {
  background: linear-gradient(135deg, #78350f 0%, #b45309 100%);
}

.tile-forest {
  background: linear-gradient(135deg, #166534 0%, #22c55e 100%);
}

/* Two-line customer cell (name in bold, phone beneath) for the Returns table. */
.cust-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.cust-cell__name {
  font-weight: 700;
  color: #1f2a44;
}

.cust-cell__phone {
  margin-top: 2px;
  font-size: 0.72rem;
  color: #6b7280;
}

/* Returned-products list inside the per-order Returns table. */
.ret-products {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 280px;
}

.ret-products__item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.78rem;
  line-height: 1.25;
}

.ret-products__name {
  color: #1f2a44;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ret-products__qty {
  flex: 0 0 auto;
  font-weight: 700;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

/* Return-status pill used by the per-order Returns table. */
.ret-pill {
  display: inline-block;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.ret-pill--full {
  background: #fee2e2;
  color: #b91c1c;
}

.ret-pill--partial {
  background: #fef3c7;
  color: #92400e;
}

/* Two-line cell used by Staff Returns to show "total" + own/c-o breakdown. */
.split-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.15;
}

.split-cell__value {
  font-weight: 600;
  color: #1f2a44;
}

.split-cell__sub {
  margin-top: 2px;
  font-size: 0.7rem;
  color: #6b7280;
  white-space: nowrap;
}

/* Muted dash for the dedicated C/O Orders column when a row has none. */
.co-muted {
  color: #b6bdc9;
}

/* Negative net c/o (orders billed but credited away) — flagged in red. */
.co-neg {
  color: #dc2626;
}

/* ── Tile skeletons (initial-load only) ─────────────────── */
.tile-skeleton {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  background: linear-gradient(135deg, #eef1f7 0%, #f7f9fc 100%);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
  position: relative;
  overflow: hidden;
}

.tile-skeleton::after {
  /* Diagonal shimmer that sweeps across each tile. The animation is
     staggered per-tile via animation-delay below so the row feels
     alive rather than pulsing in unison. */
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    transparent 30%,
    rgba(255, 255, 255, 0.72) 50%,
    transparent 70%
  );
  transform: translateX(-100%);
  animation: tile-shimmer 1.6s ease-in-out infinite;
}

.tile-skeleton__body {
  padding: 1.1rem 1.2rem;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.tile-skeleton__lines {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.tile-skeleton__line {
  height: 14px;
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.08);
}

.tile-skeleton__line--value { width: 55%; height: 18px; }
.tile-skeleton__line--label { width: 78%; height: 10px; }

.tile-skeleton__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.08);
  flex: 0 0 auto;
}

/* Stagger the shimmer across the row so it looks like a wave. */
.tile-skeleton { animation-delay: 0s; }
.tiles-row > [class*='col-']:nth-child(2)  .tile-skeleton::after { animation-delay: 0.08s; }
.tiles-row > [class*='col-']:nth-child(3)  .tile-skeleton::after { animation-delay: 0.16s; }
.tiles-row > [class*='col-']:nth-child(4)  .tile-skeleton::after { animation-delay: 0.24s; }
.tiles-row > [class*='col-']:nth-child(5)  .tile-skeleton::after { animation-delay: 0.32s; }
.tiles-row > [class*='col-']:nth-child(6)  .tile-skeleton::after { animation-delay: 0.40s; }
.tiles-row > [class*='col-']:nth-child(7)  .tile-skeleton::after { animation-delay: 0.48s; }
.tiles-row > [class*='col-']:nth-child(8)  .tile-skeleton::after { animation-delay: 0.56s; }
.tiles-row > [class*='col-']:nth-child(9)  .tile-skeleton::after { animation-delay: 0.64s; }
.tiles-row > [class*='col-']:nth-child(10) .tile-skeleton::after { animation-delay: 0.72s; }
.tiles-row > [class*='col-']:nth-child(11) .tile-skeleton::after { animation-delay: 0.80s; }
.tiles-row > [class*='col-']:nth-child(12) .tile-skeleton::after { animation-delay: 0.88s; }

@keyframes tile-shimmer {
  0%   { transform: translateX(-100%); }
  60%  { transform: translateX(100%); }
  100% { transform: translateX(100%); }
}

@media (max-width: 991px) {
  .dashboard-card ::v-deep .card-body {
    padding: 1rem;
  }
}

@media (max-width: 767px) {
  .dashboard-page {
    border-radius: 10px;
  }

  .dashboard-card ::v-deep .card-body {
    padding: 0.85rem;
  }

  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-actions {
    width: 100%;
  }

  .action-btn {
    flex: 1;
    height: 38px;
    min-width: 0;
  }

  .report-tabs {
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .report-tabs::-webkit-scrollbar {
    display: none;
  }

  .report-tab {
    padding: 0.45rem 0.85rem;
    font-size: 0.8rem;
    flex: 0 0 auto;
  }

  .tab-count {
    font-size: 0.65rem;
    padding: 0.1rem 0.4rem;
  }

  .tile-col {
    height: 86px;
  }

  .tile-body {
    padding: 0.85rem 0.9rem;
    gap: 0.5rem;
  }

  .tile-value {
    font-size: 1.1rem;
    margin-bottom: 0.2rem;
  }

  .tile-label {
    font-size: 0.7rem;
  }

  .tile-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
  }

  .tile-icon .c-icon {
    width: 17px;
    height: 17px;
  }

  .tile-skeleton__body {
    padding: 0.85rem 0.9rem;
  }
}

@media (max-width: 459px) {
  .tile-col {
    height: 80px;
  }

  .tile-body {
    padding: 0.7rem 0.75rem;
  }

  .tile-value {
    font-size: 1rem;
  }

  .tile-label {
    font-size: 0.66rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tile-icon {
    display: none;
  }

  .tile-skeleton__icon {
    display: none;
  }

  .report-tab {
    padding: 0.4rem 0.7rem;
    font-size: 0.75rem;
  }
}
</style>
